import { useCallback, useEffect, useState } from 'react';
import {
  Check,
  Clock,
  ExternalLink,
  Loader2,
  MailPlus,
  MessageCircle,
  PhoneOff,
  RefreshCw,
  TrendingUp,
  TriangleAlert,
  Wallet,
  X,
} from 'lucide-react';
import { formatarPreco, linkWhatsApp } from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { api, type CarrinhoParado, type DemonstracaoParada, type ResumoDaRecuperacao } from '@/lib/api';

/**
 * Recuperação de vendas.
 *
 * A pergunta que esta tela responde é simples: quem chegou perto de comprar e
 * não comprou? São dois casos, com conversas diferentes:
 *
 *  - **carrinho parado** — gerou o link de pagamento e não pagou. Já escolheu o
 *    plano e viu o valor; o e-mail de retomada devolve o MESMO link, então
 *    concluir é um clique;
 *  - **demonstração sem assinatura** — validou o e-mail, testou o gerador e não
 *    fechou. Aqui a conversa é sobre destravar a decisão, não apresentar o
 *    produto.
 *
 * Cada contato mostra a última vez que falamos com a pessoa, para não insistir
 * sem motivo — e o número do topo ("recuperadas") conta só o que foi pago
 * depois de um contato nosso, que é a única medida honesta do resultado.
 */
export function RecuperacaoDeVendas() {
  const [resumo, setResumo] = useState<ResumoDaRecuperacao | null>(null);
  const [carrinhos, setCarrinhos] = useState<CarrinhoParado[]>([]);
  const [demonstracoes, setDemonstracoes] = useState<DemonstracaoParada[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await api.recuperacao();
      setResumo(resposta.resumo);
      setCarrinhos(resposta.carrinhos);
      setDemonstracoes(resposta.demonstracoes);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar a recuperação de vendas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /** Envia a mensagem de recuperação e conta como foi o envio. */
  const contatar = async (tipo: 'carrinho' | 'demonstracao', id: string) => {
    setOcupado(id);
    setErro(null);
    setAviso(null);
    try {
      const resposta = await api.contatarRecuperacao(tipo, id);
      setAviso(
        resposta.envio.via === 'SMTP'
          ? `${resposta.mensagem} Enviado por e-mail.`
          : `${resposta.mensagem} ${resposta.envio.aviso ?? ''}`.trim(),
      );
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível enviar agora.');
    } finally {
      setOcupado(null);
    }
  };

  const definirStatusDoPedido = async (id: string, status: 'PENDENTE' | 'CANCELADO') => {
    setOcupado(id);
    setErro(null);
    setAviso(null);
    try {
      await api.definirStatusDoPedido(id, status);
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível atualizar o pedido.');
    } finally {
      setOcupado(null);
    }
  };

  const arquivarDemonstracao = async (id: string) => {
    setOcupado(id);
    setErro(null);
    setAviso(null);
    try {
      await api.atualizarLead(id, 'ABANDONADO');
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível arquivar o contato.');
    } finally {
      setOcupado(null);
    }
  };

  /** Dias em palavras: “hoje”, “1 dia”, “N dias”. */
  const emDias = (dias: number | null): string => {
    if (dias === null) return '—';
    if (dias <= 0) return 'hoje';
    return dias === 1 ? '1 dia' : `${dias} dias`;
  };

  /** Dias desde uma data, em palavras. */
  const desde = (iso: string | null): string => {
    if (!iso) return '—';
    return `há ${emDias(Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000))}`;
  };

  /**
   * Validade da demonstração em palavras.
   *
   * Depois que o acesso vence, a pessoa perde o que montou — é o argumento mais
   * forte para puxar a conversa, então vale mostrar aqui e não escondido.
   */
  const validadeDaDemo = (expiraEm: string | null): string => {
    if (!expiraEm) return 'sem prazo';
    const dias = Math.ceil((new Date(expiraEm).getTime() - Date.now()) / 86_400_000);
    if (dias < 0) return `expirou há ${emDias(Math.abs(dias))}`;
    if (dias === 0) return 'vence hoje';
    return `vence em ${emDias(dias)}`;
  };

  const temAlgo = carrinhos.length > 0 || demonstracoes.length > 0;

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <TrendingUp className="size-4" />
          Recuperação de vendas
        </h2>
        {resumo && resumo.carrinhos + resumo.demonstracoes > 0 ? (
          <Badge tom="atencao">{resumo.carrinhos + resumo.demonstracoes} em aberto</Badge>
        ) : null}
        <Button variant="contorno" tamanho="pequeno" className="ml-auto" onClick={carregar} disabled={carregando}>
          {carregando ? <Loader2 className="animate-spin" /> : <RefreshCw />}
          Atualizar
        </Button>
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        Quem chegou perto de comprar e não comprou. O e-mail de retomada devolve o mesmo link de pagamento do
        pedido — e o WhatsApp leva a mensagem pronta, é só enviar.
      </p>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          {erro}
        </p>
      ) : null}

      {aviso ? (
        <p className="flex items-start gap-2 rounded-lg border border-success/40 bg-success/5 p-3 text-xs text-success">
          <Check className="mt-0.5 size-3.5 shrink-0" />
          {aviso}
        </p>
      ) : null}

      {/* Indicadores: quanto está parado e quanto já voltou */}
      <div className="grid gap-3 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-card p-3">
          <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <Wallet className="size-3.5" />
            Valor parado
          </span>
          <span className="mt-1 block text-xl font-semibold tracking-tight">
            {resumo ? formatarPreco(resumo.valorParadoCentavos) : '—'}
          </span>
          <span className="mt-0.5 block text-[11px] text-muted-foreground">
            {resumo ? `${resumo.carrinhos} carrinho(s) sem pagamento` : ''}
          </span>
        </div>

        <div className="rounded-lg border border-border bg-card p-3">
          <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <Clock className="size-3.5" />
            Demonstrações sem assinatura
          </span>
          <span className="mt-1 block text-xl font-semibold tracking-tight">
            {resumo ? resumo.demonstracoes : '—'}
          </span>
          <span className="mt-0.5 block text-[11px] text-muted-foreground">validaram o e-mail e não fecharam</span>
        </div>

        <div className="rounded-lg border border-border bg-card p-3">
          <span className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            <Check className="size-3.5" />
            Recuperadas (30 dias)
          </span>
          <span className="mt-1 block text-xl font-semibold tracking-tight">
            {resumo ? resumo.recuperadas : '—'}
          </span>
          <span className="mt-0.5 block text-[11px] text-muted-foreground">
            {resumo ? `${formatarPreco(resumo.valorRecuperadoCentavos)} pagos após um contato` : ''}
          </span>
        </div>
      </div>

      {carregando && !temAlgo ? (
        <p className="flex items-center justify-center gap-2 rounded-lg border border-border p-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Procurando vendas paradas...
        </p>
      ) : null}

      {!carregando && !temAlgo ? (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
          Nenhuma venda parada agora. Quando alguém gerar o link de pagamento e não concluir, ou testar a
          demonstração e não assinar, aparece aqui — com o caminho para retomar o contato.
        </p>
      ) : null}

      {/* ---------------- Carrinhos parados ---------------- */}
      {carrinhos.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Carrinhos parados ({carrinhos.length})
          </h3>

          <ul className="grid gap-3 lg:grid-cols-2">
            {carrinhos.map((carrinho) => {
              const conversa = linkWhatsApp(
                carrinho.telefone,
                `Olá${carrinho.nome ? `, ${carrinho.nome.split(' ')[0]}` : ''}! Vi que seu pedido do plano ${
                  carrinho.nomePlano
                } ficou pela metade. Quer que eu ajude a concluir? O link é o mesmo: ${carrinho.linkCheckout ?? ''}`,
              );

              return (
                <li key={carrinho.id} className="rounded-lg border border-border bg-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="block truncate font-medium text-foreground">{carrinho.nome || '—'}</span>
                      <span className="block truncate text-xs text-muted-foreground">{carrinho.email}</span>
                    </div>
                    <div className="text-right">
                      <span className="block font-semibold tracking-tight">
                        {formatarPreco(carrinho.valorCentavos)}
                      </span>
                      <span className="text-[11px] text-muted-foreground">{carrinho.nomePlano}</span>
                    </div>
                  </div>

                  <dl className="mt-2.5 space-y-0.5 text-[11px] text-muted-foreground">
                    <div className="flex gap-2">
                      <dt>Parado</dt>
                      <dd className="ml-auto text-right">há {emDias(carrinho.diasParado)}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt>Último contato</dt>
                      <dd className="ml-auto text-right">
                        {carrinho.ultimoContatoEm
                          ? `há ${emDias(carrinho.diasDesdeOContato)}`
                          : 'ainda não falamos'}
                      </dd>
                    </div>
                  </dl>

                  {!carrinho.telefone ? (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                      <PhoneOff className="size-3" />
                      Sem telefone na conta — dá para incluir em “Usuários e acessos”.
                    </p>
                  ) : null}

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Button
                      tamanho="pequeno"
                      onClick={() => contatar('carrinho', carrinho.id)}
                      disabled={ocupado === carrinho.id}
                    >
                      {ocupado === carrinho.id ? <Loader2 className="animate-spin" /> : <MailPlus />}
                      Enviar retomada
                    </Button>

                    {carrinho.linkCheckout ? (
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <a href={carrinho.linkCheckout} target="_blank" rel="noreferrer">
                          <ExternalLink />
                          Abrir o link
                        </a>
                      </Button>
                    ) : null}

                    {conversa ? (
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <a href={conversa} target="_blank" rel="noreferrer">
                          <MessageCircle />
                          WhatsApp
                        </a>
                      </Button>
                    ) : null}

                    <Button
                      variant="fantasma"
                      tamanho="pequeno"
                      onClick={() => definirStatusDoPedido(carrinho.id, 'CANCELADO')}
                      disabled={ocupado === carrinho.id}
                      title="Tira da lista sem apagar o histórico do pedido."
                    >
                      <X />
                      Marcar como perdido
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}

      {/* ---------------- Demonstrações sem assinatura ---------------- */}
      {demonstracoes.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Testaram e não assinaram ({demonstracoes.length})
          </h3>

          <ul className="grid gap-3 lg:grid-cols-2">
            {demonstracoes.map((pessoa) => {
              const conversa = linkWhatsApp(
                pessoa.telefone,
                `Olá${pessoa.nome && pessoa.nome !== '—' ? `, ${pessoa.nome.split(' ')[0]}` : ''}! Você testou o Miolix e não chegou a assinar. Posso te ajudar a escolher um plano?`,
              );

              /** Quem já criou miolo de verdade está muito mais perto de fechar. */
              const usouDeVerdade = pessoa.miolos > 0;

              return (
                <li key={pessoa.id} className="rounded-lg border border-border bg-card p-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className="block truncate font-medium text-foreground">{pessoa.nome}</span>
                      <span className="block truncate text-xs text-muted-foreground">{pessoa.email}</span>
                    </div>
                    <Badge tom={usouDeVerdade ? 'principal' : 'neutro'}>
                      {pessoa.miolos === 0
                        ? 'não criou miolo'
                        : `${pessoa.miolos} miolo(s) criado(s)`}
                    </Badge>
                  </div>

                  <dl className="mt-2.5 space-y-0.5 text-[11px] text-muted-foreground">
                    <div className="flex gap-2">
                      <dt>Validou o e-mail</dt>
                      <dd className="ml-auto text-right">há {emDias(pessoa.diasParado)}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt>Último acesso</dt>
                      <dd className="ml-auto text-right">
                        {pessoa.ultimoAcessoEm ? desde(pessoa.ultimoAcessoEm) : 'nunca entrou'}
                      </dd>
                    </div>
                    <div className="flex gap-2">
                      <dt>Demonstração</dt>
                      <dd className="ml-auto text-right">{validadeDaDemo(pessoa.expiraEm)}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt>Convite enviado</dt>
                      <dd className="ml-auto text-right">
                        {pessoa.ultimoEnvioEm ? desde(pessoa.ultimoEnvioEm) : 'ainda não'}
                      </dd>
                    </div>
                  </dl>

                  {!pessoa.telefone ? (
                    <p className="mt-2 flex items-center gap-1.5 text-[11px] text-amber-700 dark:text-amber-400">
                      <PhoneOff className="size-3" />
                      Sem telefone na conta — dá para incluir em “Usuários e acessos”.
                    </p>
                  ) : null}

                  <div className="mt-3 flex flex-wrap gap-1.5">
                    <Button
                      tamanho="pequeno"
                      onClick={() => contatar('demonstracao', pessoa.id)}
                      disabled={ocupado === pessoa.id}
                    >
                      {ocupado === pessoa.id ? <Loader2 className="animate-spin" /> : <MailPlus />}
                      Enviar convite
                    </Button>

                    {conversa ? (
                      <Button asChild variant="contorno" tamanho="pequeno">
                        <a href={conversa} target="_blank" rel="noreferrer">
                          <MessageCircle />
                          WhatsApp
                        </a>
                      </Button>
                    ) : null}

                    <Button
                      variant="fantasma"
                      tamanho="pequeno"
                      onClick={() => arquivarDemonstracao(pessoa.id)}
                      disabled={ocupado === pessoa.id}
                      title="Tira da lista sem apagar o contato do funil."
                    >
                      <X />
                      Marcar como perdida
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
}
